import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, getDocs, collection } from 'firebase/firestore';
import { db } from '../../firebase';
import { useTasks } from '../../context/TasksContext';
import { useCompany } from '../../context/CompanyContext';
import { useRole } from '../../context/RoleContext';
import { useAuth } from '../../context/AuthContext';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { CalendarIcon, Clock, Edit2, Trash2, Plus } from 'lucide-react';

const TASK_TYPES = [
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'visit', label: 'Visit' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const TASK_CATEGORIES = [
  { value: 'internal', label: 'Internal Task' },
  { value: 'job', label: 'Job / Appointment' },
];

const LeadTasksPanel = ({ leadId, compact = false, showCompleted = true, showExistingTasks = true }) => {
  const { tasks, addTask, updateTask, deleteTask, completeJob, getTasksForLead } = useTasks();
  const { companyId } = useCompany();
  const { isOwner, isAdmin, isSales } = useRole();
  const { user } = useAuth();

  const [companyUsers, setCompanyUsers] = useState([]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        if (!companyId) {
          setCompanyUsers([]);
          return;
        }
        const membershipsSnapshot = await getDocs(collection(db, 'companies', companyId, 'users'));
        const memberships = membershipsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        const joined = await Promise.all(
          memberships.map(async (m) => {
            try {
              const profileSnap = await getDoc(doc(db, 'users', m.id));
              const profile = profileSnap.exists() ? profileSnap.data() : {};
              return { id: m.id, role: m.role, ...profile };
            } catch (e) {
              return { id: m.id, role: m.role };
            }
          })
        );
        setCompanyUsers(joined);
      } catch (err) {
        console.error('Failed to load company users for task assignment', err);
        setCompanyUsers([]);
      }
    };

    loadUsers();
  }, [companyId]);

  const [showForm, setShowForm] = useState(!showExistingTasks);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [formState, setFormState] = useState({
    title: '',
    description: '',
    taskType: 'call',
    priority: 'medium',
    taskCategory: 'internal',
    dueDate: '',
    dueTime: '',
    scheduledDate: '',
    scheduledStartTime: '',
    scheduledEndTime: '',
    assignedTo: '',
  });

  const leadTasks = useMemo(() => getTasksForLead(leadId), [getTasksForLead, leadId, tasks]);

  // Sort: pending overdue, pending upcoming, then completed
  const sortedTasks = useMemo(() => {
    if (!showExistingTasks) return [];

    const now = new Date();

    const parseDateTime = (task) => {
      if (!task.dueDate) return null;
      const iso = task.dueTime ? `${task.dueDate}T${task.dueTime}:00` : `${task.dueDate}T00:00:00`;
      const d = new Date(iso);
      return isNaN(d.getTime()) ? null : d;
    };

    const pending = [];
    const completed = [];

    for (const task of leadTasks) {
      if (task.status === 'completed') {
        completed.push(task);
      } else {
        pending.push(task);
      }
    }

    const compareByDue = (a, b) => {
      const aDate = parseDateTime(a);
      const bDate = parseDateTime(b);
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return aDate - bDate;
    };

    const pendingOverdue = pending.filter((t) => {
      const dt = parseDateTime(t);
      return dt && dt < now;
    });
    const pendingUpcoming = pending.filter((t) => {
      const dt = parseDateTime(t);
      return !dt || dt >= now;
    });

    pendingOverdue.sort(compareByDue);
    pendingUpcoming.sort(compareByDue);
    completed.sort(compareByDue);

    if (!showCompleted) {
      return [...pendingOverdue, ...pendingUpcoming];
    }

    return [...pendingOverdue, ...pendingUpcoming, ...completed];
  }, [leadTasks, showCompleted, showExistingTasks]);

  const resetForm = () => {
    setFormState({
      title: '',
      description: '',
      taskType: 'call',
      priority: 'medium',
      taskCategory: 'internal',
      dueDate: '',
      dueTime: '',
      scheduledDate: '',
      scheduledStartTime: '',
      scheduledEndTime: '',
      assignedTo: '',
    });
    setEditingTaskId(null);
  };

  const startEdit = (task) => {
    setEditingTaskId(task.id);
    setFormState({
      title: task.title || '',
      description: task.description || '',
      taskType: task.taskType || 'call',
      priority: task.priority || 'medium',
      taskCategory: task.taskCategory || 'internal',
      dueDate: task.dueDate || '',
      dueTime: task.dueTime || '',
      scheduledDate: task.scheduledStart ? task.scheduledStart.slice(0, 10) : '',
      scheduledStartTime: task.scheduledStart ? task.scheduledStart.slice(11, 16) : '',
      scheduledEndTime: task.scheduledEnd ? task.scheduledEnd.slice(11, 16) : '',
      assignedTo: task.assignedTo || '',
    });
    setShowForm(true);
  };

  const handleToggleStatus = async (task) => {
    const newStatus = task.status === 'pending' ? 'completed' : 'pending';
    // For job-type tasks being marked complete, use completeJob for auto-invoice
    if (newStatus === 'completed' && (task.type === 'job' || task.taskCategory === 'job')) {
      await completeJob(task.id, { quoteId: task.quoteId });
    } else {
      await updateTask(task.id, { status: newStatus });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formState.title.trim()) return;

    const isJob = formState.taskCategory === 'job';

    const scheduledStart = isJob && formState.scheduledDate && formState.scheduledStartTime
      ? `${formState.scheduledDate}T${formState.scheduledStartTime}`
      : null;
    const scheduledEnd = isJob && formState.scheduledDate && formState.scheduledEndTime
      ? `${formState.scheduledDate}T${formState.scheduledEndTime}`
      : null;

    const payload = {
      leadId,
      title: formState.title.trim(),
      description: formState.description.trim() || '',
      taskType: formState.taskType,
      priority: formState.priority,
      taskCategory: formState.taskCategory,
      type: formState.taskCategory === 'job' ? 'job' : 'task',
      dueDate: !isJob ? formState.dueDate || null : null,
      dueTime: !isJob ? formState.dueTime || null : null,
      scheduledStart,
      scheduledEnd,
      assignedTo: formState.assignedTo === 'unassigned' ? null : (formState.assignedTo || null),
    };

    if (editingTaskId) {
      await updateTask(editingTaskId, payload);
    } else {
      await addTask(payload);
    }

    resetForm();
    setShowForm(false);
  };

  const renderTaskRow = (task) => {
    const isCompleted = task.status === 'completed';
    const isJob = task.type === 'job';

    const scheduleLabel = (() => {
      if (isJob && task.scheduledStart) {
        const iso = task.scheduledStart;
        const date = iso.slice(0, 10);
        const time = iso.slice(11, 16);
        return `${date}${time ? ` · ${time}` : ''}`;
      }
      if (!isJob && task.dueDate) {
        return `${task.dueDate}${task.dueTime ? ` · ${task.dueTime}` : ''}`;
      }
      return 'No schedule';
    })();

    return (
      <div
        key={task.id}
        className={`flex flex-col gap-1 border-b border-gray-200 pb-2 last:border-b-0 ${
          isCompleted ? 'opacity-60' : ''
        }`}
      >
        <div className="flex items-center gap-2">
          <Checkbox
            checked={isCompleted}
            onCheckedChange={() => handleToggleStatus(task)}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={`text-sm truncate ${isCompleted ? 'line-through text-gray-500' : 'text-gray-900'}`}
              >
                {task.title}
              </span>
              {task.taskType && (
                <Badge variant="outline" className="text-[10px] px-1 py-0">
                  {task.taskType}
                </Badge>
              )}
              {task.priority && (
                <Badge
                  variant="outline"
                  className={`text-[10px] px-1 py-0 ${
                    task.priority === 'high'
                      ? 'border-red-500 text-red-600'
                      : task.priority === 'low'
                      ? 'border-gray-400 text-gray-500'
                      : 'border-yellow-500 text-yellow-600'
                  }`}
                >
                  {task.priority}
                </Badge>
              )}
            </div>
            {task.description && (
              <p className="text-xs text-gray-500 truncate">{task.description}</p>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <CalendarIcon className="h-3 w-3" />
              {scheduleLabel}
            </span>
            <button
              type="button"
              onClick={() => startEdit(task)}
              className="p-1 rounded hover:bg-gray-100"
              title="Edit task"
            >
              <Edit2 className="h-3 w-3 text-gray-500" />
            </button>
            <button
              type="button"
              onClick={() => deleteTask(task.id)}
              className="p-1 rounded hover:bg-red-50"
              title="Delete task"
            >
              <Trash2 className="h-3 w-3 text-red-500" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2 text-xs">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="font-semibold text-[var(--cb-navy)] text-xs">Tasks</span>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1 text-[10px] text-[var(--cb-orange)] hover:underline"
          >
            <Plus className="h-3 w-3" />
            Add Task
          </button>
        )}
      </div>

      {/* Task List */}
      {showExistingTasks && (
        <div className={compact ? 'max-h-40 overflow-y-auto pr-1 space-y-2' : 'space-y-2'}>
          {sortedTasks.length === 0 && !showForm && (
            <p className="text-[10px] text-gray-400">No tasks yet</p>
          )}
          {sortedTasks.map(renderTaskRow)}
        </div>
      )}

      {/* Add/Edit Task Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mt-2 space-y-2 border-t border-gray-200 pt-2">
          <Input
            placeholder="Task title"
            value={formState.title}
            onChange={(e) => setFormState((prev) => ({ ...prev, title: e.target.value }))}
            className="h-7 text-xs"
          />

          {/* Category & Assignment */}
          <div className="grid grid-cols-2 gap-2">
            <Select
              value={formState.taskCategory}
              onValueChange={(value) => setFormState((prev) => ({ ...prev, taskCategory: value }))}
            >
              <SelectTrigger className="h-7 text-[10px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {TASK_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value} className="text-xs">
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(isOwner || isAdmin || isSales) && (
              <Select
                value={formState.assignedTo}
                onValueChange={(value) => setFormState((prev) => ({ ...prev, assignedTo: value }))}
              >
                <SelectTrigger className="h-7 text-[10px]">
                  <SelectValue placeholder="Assign to" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {companyUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id} className="text-xs">
                      {u.displayName || u.name || u.email || u.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Scheduling */}
          {formState.taskCategory === 'job' ? (
            <div className="grid grid-cols-3 gap-2">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-gray-400" />
                <Input
                  type="date"
                  value={formState.scheduledDate}
                  onChange={(e) => setFormState((prev) => ({ ...prev, scheduledDate: e.target.value }))}
                  className="h-7 text-xs"
                />
              </div>
              <div className="flex items-center gap-1">
                <Input
                  type="time"
                  value={formState.scheduledStartTime}
                  onChange={(e) => setFormState((prev) => ({ ...prev, scheduledStartTime: e.target.value }))}
                  className="h-7 text-xs pr-10 overflow-visible"
                />
              </div>
              <div className="flex items-center gap-1">
                <Input
                  type="time"
                  placeholder="End (optional)"
                  value={formState.scheduledEndTime}
                  onChange={(e) => setFormState((prev) => ({ ...prev, scheduledEndTime: e.target.value }))}
                  className="h-7 text-xs pr-10 overflow-visible"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-gray-400" />
                <Input
                  type="date"
                  value={formState.dueDate}
                  onChange={(e) => setFormState((prev) => ({ ...prev, dueDate: e.target.value }))}
                  className="h-7 text-xs"
                />
              </div>
              <div className="flex items-center gap-1">
                <Input
                  type="time"
                  value={formState.dueTime}
                  onChange={(e) => setFormState((prev) => ({ ...prev, dueTime: e.target.value }))}
                  className="h-7 text-xs pr-10 overflow-visible"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Select
              value={formState.taskType}
              onValueChange={(value) => setFormState((prev) => ({ ...prev, taskType: value }))}
            >
              <SelectTrigger className="h-7 text-[10px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {TASK_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value} className="text-xs">
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={formState.priority}
              onValueChange={(value) => setFormState((prev) => ({ ...prev, priority: value }))}
            >
              <SelectTrigger className="h-7 text-[10px]">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={p.value} className="text-xs">
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Textarea
            placeholder="Description (optional)"
            value={formState.description}
            onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
            className="text-xs min-h-[40px]"
          />

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2 text-[10px]"
              onClick={() => {
                resetForm();
                setShowForm(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              className="h-7 px-3 text-[10px] text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
            >
              {editingTaskId ? 'Save' : 'Add Task'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default LeadTasksPanel;
